/**
 * Recovery for stale content-hashed chunks after a deploy.
 *
 * When the server ships a new build, the previous build's content-hashed chunk
 * filenames are deleted. A browser still running the old shell then 404s on a
 * dynamic import and throws "Failed to fetch dynamically imported module"
 * (ServiceDesk 130730). We recover by reloading the page once to pick up the
 * current shell — guarded by a short cooldown so a genuinely-broken deploy
 * cannot put the tab into a reload loop.
 */

const RELOAD_FLAG = "chunkReloadAt"
const RELOAD_COOLDOWN_MS = 30_000

/** True once this page load has scheduled a stale-chunk reload (before navigation). */
let reloadPending = false

/**
 * Reload once to pick up the current build. Returns true if a reload was
 * triggered, false if suppressed (very recent reload, or no `window`).
 */
export function reloadOnceForStaleChunk(): boolean {
  if (typeof window === "undefined") return false
  try {
    const last = Number(sessionStorage.getItem(RELOAD_FLAG) ?? 0)
    if (last && Date.now() - last < RELOAD_COOLDOWN_MS) {
      // We already reloaded moments ago and the chunk is still missing — this is
      // a broken deploy, not a merely-stale tab. Stop so we don't loop.
      return false
    }
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()))
  } catch {
    // sessionStorage unavailable (private mode): fall through and reload anyway.
  }
  reloadPending = true
  window.location.reload()
  return true
}

const CHUNK_ERROR_RE =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|dynamically imported module/i

/** True if an error looks like a stale/missing dynamic-import chunk. */
export function isChunkLoadError(error: unknown): boolean {
  let msg = ""
  if (typeof error === "string") {
    msg = error
  } else if (error instanceof Error) {
    msg = error.message
  } else {
    msg = (error as { message?: string } | null)?.message ?? ""
  }
  return CHUNK_ERROR_RE.test(msg)
}

let installed = false

/**
 * Install the global stale-chunk recovery handler. Idempotent and a no-op
 * outside the browser. Vite dispatches `vite:preloadError` on `window` when a
 * dynamic import fails to load; we swallow it and reload once.
 */
export function installChunkReloadHandler(): void {
  if (installed) return
  if (typeof window === "undefined") return
  installed = true
  window.addEventListener("vite:preloadError", (event: Event) => {
    // Prevent Vite's default (which rethrows) and recover by reloading once.
    event.preventDefault()
    reloadOnceForStaleChunk()
  })
}

/**
 * Run a dynamic import with stale-chunk recovery: retry once for a transient
 * network blip, then trigger a guarded one-time reload if it still looks like a
 * missing chunk. Usage: `await safeImport(() => import("some-module"))`.
 *
 * When a reload is triggered the returned promise intentionally never resolves,
 * so the caller does not render an error in the brief window before navigation.
 */
export async function safeImport<T>(factory: () => Promise<T>): Promise<T> {
  try {
    return await factory()
  } catch (error) {
    if (!isChunkLoadError(error)) throw error
    try {
      return await factory()
    } catch (retryError) {
      if (isChunkLoadError(retryError)) {
        if (reloadOnceForStaleChunk() || reloadPending) {
          return await new Promise<T>(() => {})
        }
      }
      throw retryError
    }
  }
}
